// filepath: /c:/Users/MANJA NAIK/react-frontend/ams/src/components/GanttChartComponent.js
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { Gantt, ViewMode, Task } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import config from '../config/config'; 
import axios from 'axios';
import * as XLSX from 'xlsx';
import { formatDate, formatNumber, formatCurrency } from '../utils/formatterUtils'; // Import the formatter utilities

const GanttChartComponent = ({ projectId }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fetchedMilestones, setFetchedMilestones] = useState(new Set());

  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        const token = localStorage.getItem('authToken');
        console.log('Retrieved Token:', token); // Log the token to verify its correctness
        if (!token) {
          throw new Error('No token found');
        }
        const response = await axios.get(`${config.apiBaseUrl}/api/milestones/project/${projectId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
        });
        const milestones = response.data;
        await Promise.all(milestones.map(milestone => fetchTasks(milestone.id, token)));
      } catch (error) {
        setError('Error fetching milestones. Please try again later.');
        setLoading(false);
      }
    };

    const fetchTasks = async (milestoneId, token) => {
      if (fetchedMilestones.has(milestoneId)) {
        console.log(`Tasks for milestone ${milestoneId} have already been fetched.`);
        return;
      }

      try {
        const response = await axios.get(`${config.apiBaseUrl}/api/tasks/milestone/${milestoneId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
        });
        const tasksData = response.data.map(task => {
          const start = new Date(task.startDate);
          const end = new Date(task.endDate);
          if (!task.startDate || !task.endDate || isNaN(start.getTime()) || isNaN(end.getTime())) {
            console.error('Invalid date for task:', task);
            return null; // Skip invalid tasks
          }
          return {
            start,
            end,
            name: task.name,
            id: task.id,
            type: 'task',
            progress: task.progress,
            isDisabled: false,
            styles: {
              backgroundColor: getStatusColor(task.status),
              color: getStatusColor(task.status), // Set text color based on status
            },
          };
        }).filter(task => task !== null); // Filter out invalid tasks

        if (tasksData.length === 0) {
          console.log(`No tasks found for milestone ${milestoneId}. Skipping.`);
          return;
        }

        setTasks(prevTasks => {
          const updatedTasks = [...prevTasks, ...tasksData];
          updatedTasks.sort((a, b) => a.start - b.start); // Sort tasks by start date
          return updatedTasks;
        });
        setFetchedMilestones(prev => new Set(prev).add(milestoneId));
        setLoading(false);
      } catch (error) {
        setError('Error fetching tasks. Please try again later.');
        setLoading(false);
      }
    };

    const getStatusColor = (status) => {
        switch (status) {
          case 'In Progress':
            return 'orange';
          case 'Completed':
            return 'green';
          case 'Delayed':
            return 'lightcoral';
          default:
            return 'black';
        }
      };

    fetchMilestones();
  }, [projectId]);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(tasks.map(task => ({
      Name: task.name,
      Start: formatDate(task.start, config.defaultLocale),
      End: formatDate(task.end, config.defaultLocale),
      Progress: formatNumber(task.progress, config.defaultLocale),
      Status: task.styles.backgroundColor,
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tasks');
    XLSX.writeFile(workbook, 'GanttChartTasks.xlsx');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const renderTask = (task: Task) => {
    return (
      <g>
        <rect
          x={task.x1}
          y={task.y}
          width={task.x2 - task.x1}
          height={task.height}
          fill={task.styles.backgroundColor}
          stroke="black"
        />
        <text
          x={(task.x1 + task.x2) / 2}
          y={task.y + task.height / 2}
          fill={task.styles.color}
          textAnchor="middle"
          alignmentBaseline="middle"
        >
          {task.name}
        </text>
      </g>
    );
  };

  const customLocale = {
    months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    days: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    today: "Today",
    task: "Task",
    start: "Start",
    end: "End",
    date: "Date",
    month: "Month",
    year: "Year",
    day: "Day",
    week: "Week",
    quarterShort: "Q",
    quarterLong: "Quarter",
  };

  const customDateFormat = (date) => {
    return formatDate(date, config.defaultLocale); // Use locale from config
  };

  return (
    <Box>
      <Button onClick={exportToExcel} variant="contained" color="primary" style={{ marginBottom: '10px' }}>
        Export to Excel
      </Button>
      <Gantt
        tasks={tasks}
        viewMode={ViewMode.Month}
        renderTask={renderTask}
        locale={customLocale}
        dateFormat={customDateFormat}
      />
    </Box>
  );
};

export default GanttChartComponent;